/**
 * Driver Apply to Water Company Page
 * صفحة تقديم السائق للانضمام لشركة مياه
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useDriverAuth } from '../../context/DriverAuthContext';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8002/api/v1';
const call = async (method, path, body, token) => {
  const r = await fetch(`${API}${path}`, {
    method,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: body ? JSON.stringify(body) : undefined,
  });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(d.detail || 'خطأ في الاتصال');
  return d;
};

const WATER_TYPES = [
  { key: 'drinking',     label: 'مياه شرب',               icon: '💧', cls: 'border-blue-300 bg-blue-50 text-blue-700' },
  { key: 'non_drinking', label: 'مياه غير صالحة للشرب',   icon: '🚰', cls: 'border-yellow-300 bg-yellow-50 text-yellow-700' },
  { key: 'treated',      label: 'مياه صرف صحي معالجة',    icon: '♻️', cls: 'border-green-300 bg-green-50 text-green-700' },
];

const STATUS_MAP = {
  pending:   { label: 'قيد المراجعة', cls: 'bg-amber-50 text-amber-700 border-amber-200'   },
  approved:  { label: 'مقبول',        cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  rejected:  { label: 'مرفوض',       cls: 'bg-red-50 text-red-700 border-red-200'         },
  suspended: { label: 'موقوف',       cls: 'bg-gray-100 text-gray-600 border-gray-200'     },
};

export default function ApplyPage() {
  const { token, driver } = useDriverAuth();

  const [tab, setTab] = useState('companies'); // companies | myapps
  const [companies, setCompanies] = useState([]);
  const [myApps, setMyApps] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);

  // Apply modal state
  const [applying, setApplying] = useState(null); // company object
  const [selectedWaterTypes, setSelectedWaterTypes] = useState([]);
  const [selectedVehicle, setSelectedVehicle] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const showToast = (msg, ok = true) => {
    setToast({ msg, ok });
    setTimeout(() => setToast(null), 3000);
  };

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [comps, apps, vehs] = await Promise.all([
        call('GET', '/driver/applications/available-companies', null, token),
        call('GET', '/driver/applications', null, token),
        call('GET', '/driver/vehicles', null, token).catch(() => []),
      ]);
      setCompanies(comps || []);
      setMyApps(apps || []);
      setVehicles(vehs || []);
    } catch (e) {
      showToast(e.message, false);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const openApply = (company) => {
    if (company.already_applied) return;
    setApplying(company);
    setSelectedWaterTypes([]);
    setSelectedVehicle('');
    setNotes('');
  };

  const toggleWater = (key) => {
    setSelectedWaterTypes(prev =>
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    );
  };

  const submitApply = async () => {
    if (selectedWaterTypes.length === 0) {
      showToast('حدد نوع المياه الذي ستعمل عليه', false); return;
    }
    setSubmitting(true);
    try {
      const res = await call('POST', '/driver/applications', {
        tenant_id: applying.id,
        water_types: selectedWaterTypes,
        vehicle_id: selectedVehicle || null,
        notes: notes.trim() || null,
      }, token);
      showToast(res.message);
      setApplying(null);
      loadAll();
    } catch (e) {
      showToast(e.message, false);
    } finally {
      setSubmitting(false);
    }
  };

  const cancelApp = async (appId) => {
    try {
      await call('DELETE', `/driver/applications/${appId}`, null, token);
      showToast('تم إلغاء الطلب');
      loadAll();
    } catch (e) { showToast(e.message, false); }
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-24" dir="rtl">
      {/* Toast */}
      {toast && (
        <div className={`fixed top-4 left-1/2 -translate-x-1/2 z-50 px-5 py-2.5 rounded-xl shadow-lg text-sm font-bold
          ${toast.ok ? 'bg-emerald-500 text-white' : 'bg-red-500 text-white'}`}>
          {toast.msg}
        </div>
      )}

      {/* Header */}
      <div className="bg-white border-b border-gray-100 px-4 pt-safe pb-3 sticky top-0 z-20">
        <h1 className="text-lg font-black text-gray-900">التقديم لشركات المياه</h1>
        <p className="text-xs text-gray-500">تقدّم للانضمام واختر نوع المياه الذي تعمل عليه</p>
      </div>

      {/* Tabs */}
      <div className="flex bg-white border-b border-gray-100 px-4">
        {[
          { key: 'companies', label: 'الشركات المتاحة' },
          { key: 'myapps',    label: `طلباتي (${myApps.length})` },
        ].map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`px-4 py-3 text-sm font-bold border-b-2 transition-colors ${
              tab === t.key ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500'}`}>
            {t.label}
          </button>
        ))}
      </div>

      <div className="p-4 space-y-3">
        {loading ? (
          <div className="flex justify-center py-16">
            <div className="w-7 h-7 border-2 border-gray-200 border-t-blue-500 rounded-full animate-spin"/>
          </div>
        ) : tab === 'companies' ? (
          <>
            {companies.length === 0 ? (
              <div className="text-center py-16 text-gray-400">
                <p className="text-3xl mb-2">🏭</p>
                <p className="text-sm font-bold">لا توجد شركات متاحة حالياً</p>
              </div>
            ) : companies.map(c => (
              <div key={c.id} className="bg-white rounded-2xl border border-gray-100 p-4 shadow-sm">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <p className="font-black text-gray-900 text-base">{c.company_name}</p>
                    {c.city && <p className="text-xs text-gray-400 mt-0.5">📍 {c.city}</p>}
                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                      <span className="text-xs text-gray-500 bg-gray-50 border border-gray-100 px-2 py-1 rounded-lg">
                        🚢 {c.ship_count} محطة
                      </span>
                      {(c.water_types || []).map(wt => {
                        const w = WATER_TYPES.find(x => x.key === wt);
                        return w ? (
                          <span key={wt} className={`text-xs px-2 py-1 rounded-lg border font-medium ${w.cls}`}>
                            {w.icon} {w.label}
                          </span>
                        ) : null;
                      })}
                    </div>
                  </div>
                  {c.already_applied ? (
                    <span className="text-xs text-gray-400 bg-gray-100 px-3 py-1.5 rounded-xl font-bold whitespace-nowrap">
                      تم التقديم
                    </span>
                  ) : (
                    <button onClick={() => openApply(c)}
                      className="px-4 py-2 rounded-xl bg-blue-500 text-white text-xs font-black hover:bg-blue-600 active:scale-95 transition-all whitespace-nowrap shadow-sm">
                      تقدّم الآن
                    </button>
                  )}
                </div>
              </div>
            ))}
          </>
        ) : (
          <>
            {myApps.length === 0 ? (
              <div className="text-center py-16 text-gray-400">
                <p className="text-3xl mb-2">📋</p>
                <p className="text-sm font-bold">لم تتقدم لأي شركة بعد</p>
              </div>
            ) : myApps.map(app => {
              const st = STATUS_MAP[app.status] || STATUS_MAP.pending;
              return (
                <div key={app.id} className="bg-white rounded-2xl border border-gray-100 p-4 shadow-sm">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-black text-gray-900">{app.company_name}</p>
                        <span className={`px-2.5 py-0.5 rounded-lg text-[11px] font-bold border ${st.cls}`}>
                          {st.label}
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {(app.water_types || []).map(wt => {
                          const w = WATER_TYPES.find(x => x.key === wt);
                          return w ? (
                            <span key={wt} className={`text-[11px] px-2 py-0.5 rounded-lg border ${w.cls}`}>
                              {w.icon} {w.label}
                            </span>
                          ) : null;
                        })}
                      </div>
                      {app.rejection_reason && (
                        <p className="text-xs text-red-600 bg-red-50 rounded-lg px-2.5 py-1.5 mt-2">
                          ❌ {app.rejection_reason}
                        </p>
                      )}
                      <p className="text-[11px] text-gray-400 mt-2">
                        {app.created_at ? new Date(app.created_at).toLocaleDateString('ar-SA') : ''}
                      </p>
                    </div>
                    {app.status === 'pending' && (
                      <button onClick={() => cancelApp(app.id)}
                        className="text-xs text-red-500 hover:text-red-700 px-2 py-1 rounded-lg hover:bg-red-50 transition-colors">
                        إلغاء
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </>
        )}
      </div>

      {/* Apply Modal */}
      {applying && (
        <div className="fixed inset-0 z-50 flex items-end" dir="rtl">
          <div className="absolute inset-0 bg-black/40" onClick={() => setApplying(null)}/>
          <div className="relative w-full bg-white rounded-t-3xl p-5 space-y-5 shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="w-10 h-1 bg-gray-200 rounded-full mx-auto mb-2"/>
            <div>
              <h2 className="text-lg font-black text-gray-900">التقديم لـ {applying.company_name}</h2>
              <p className="text-sm text-gray-500">اختر نوع المياه والمركبة التي ستعمل بها</p>
            </div>

            {/* Water types */}
            <div>
              <p className="text-sm font-bold text-gray-700 mb-3">نوع المياه * (يمكن اختيار أكثر من نوع)</p>
              <div className="space-y-2">
                {WATER_TYPES.map(wt => (
                  <button key={wt.key} onClick={() => toggleWater(wt.key)}
                    className={`w-full flex items-center gap-3 p-3.5 rounded-xl border-2 transition-all text-right
                      ${selectedWaterTypes.includes(wt.key)
                        ? 'border-blue-400 bg-blue-50 shadow-sm'
                        : 'border-gray-100 bg-white hover:border-gray-200'}`}>
                    <span className="text-2xl">{wt.icon}</span>
                    <div className="flex-1">
                      <p className="font-bold text-gray-900 text-sm">{wt.label}</p>
                    </div>
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0
                      ${selectedWaterTypes.includes(wt.key) ? 'border-blue-500 bg-blue-500' : 'border-gray-300'}`}>
                      {selectedWaterTypes.includes(wt.key) && (
                        <svg className="w-3 h-3 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                          <polyline points="20 6 9 17 4 12"/>
                        </svg>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Vehicle select */}
            {vehicles.length > 0 && (
              <div>
                <p className="text-sm font-bold text-gray-700 mb-2">المركبة (اختياري)</p>
                <select value={selectedVehicle} onChange={e => setSelectedVehicle(e.target.value)}
                  className="w-full px-3 py-3 border border-gray-200 rounded-xl text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-300">
                  <option value="">— اختر مركبة —</option>
                  {vehicles.map(v => (
                    <option key={v.id} value={v.id}>
                      {v.make} {v.model} — {v.plate_number || v.plate_number_ar || ''}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Notes */}
            <div>
              <p className="text-sm font-bold text-gray-700 mb-2">ملاحظات (اختياري)</p>
              <textarea value={notes} onChange={e => setNotes(e.target.value)}
                rows={2} placeholder="أي معلومات إضافية تريد إضافتها..."
                className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-300"/>
            </div>

            {/* Submit */}
            <div className="flex gap-3 pb-safe">
              <button onClick={submitApply} disabled={submitting || selectedWaterTypes.length === 0}
                className={`flex-1 py-3.5 rounded-xl text-white font-black text-sm transition-all
                  ${selectedWaterTypes.length === 0 || submitting ? 'bg-gray-300' : 'bg-blue-500 hover:bg-blue-600 active:scale-[0.98] shadow-md'}`}>
                {submitting
                  ? <span className="inline-block w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"/>
                  : 'تقديم الطلب'}
              </button>
              <button onClick={() => setApplying(null)}
                className="px-5 py-3.5 rounded-xl bg-gray-100 text-gray-600 font-bold text-sm">
                إلغاء
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
